import Box from 'common/components/box/Box';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import styles from './VerificationScreen.module.scss';
import CarVerification from 'screens/vehicle/components/carVerificationTab/components/carVerification/CarVerification';
import Popover from 'common/components/popover/Popover';
import ScreenHeaderButton from 'common/components/screenHeader/ScreenHeaderButton';
import AddIcon from 'assets/svg/desktop_add_new.svg';
import { CarVerificationSearchCriteria } from 'api/carVerification/models/CarVerificationSearchCriteria';
import { DATE_FORMAT_DEFAUT, DEFAULT_PAGINATION_ITEMS_PER_PAGE } from 'Config';
import VerificationFiltersScreen, { Filters } from './components/list-filters/VerificationFiltersScreen';
import FiltersIcon from 'assets/svg/desktop_filtres.svg';
import { UserProfile } from 'api/account/models/UserProfile';
import UsersService from 'api/users/UsersService';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import FilterBadgesOverflow, { FilterBadgeItem } from 'common/components/filterBadgesOverflow/FilterBadgesOverflow';
import { CompactPageLayout } from 'common/components/compactPageLayout';
import ExportListingButton from 'common/components/exportListingButton/ExportListingButton';
import ListVerificationsScreen from './components/listVerifications/ListVerificationsScreen';
import { CarVerificationListItemDto } from 'api/carVerification/models/CarVerificationDto';
import dayjs from 'dayjs';

type RouteProps = {
    itemId: string;
    type: 'new' | 'edit' | 'details';
}

const VerificationScreen = () => {
    const { itemId, type } = useParams<RouteProps>();

    const FILTERS_CACHE_KEY = 'VERIFICATIONSFILTER';
    const isFirstLoad = useRef<boolean>(true);
    const history = useHistory();
    const { t } = useTranslation();

    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasVerificationsWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_VERIFICATIONS_WRITE']);
    const exportListingRef = useRef<{ exportExcel: () => Promise<void> } | null>(null);

    const [filters, setFilters] = useState<Filters>({});
    const [filtersTotal, setFiltersTotal] = useState(0);

    const [criteria, setCriteria] = useState<CarVerificationSearchCriteria>({
        itemsPerPage: DEFAULT_PAGINATION_ITEMS_PER_PAGE,
        page: 1,
        orderColumn: 'v.verification_date',
        orderBy: 'desc',
    });

    const updateTotalFilters = (f: Filters) => {
        let total = 0;
        if (f.vehicleId) total++;
        if (f.type) total++;
        if (f.driverResponsibleId) total++;
        if (f.startDate || f.endDate) total++;
        if (f.interventionType) total++;
        if (f.annotationStatus) total++;
        if (f.tripStartDate) total++;
        if (f.tripEndDate) total++;
        if (f.segmentId) total++;
        if (f.localIds?.length) total++;
        setFiltersTotal(total);
    };

    const loadFilters = () => {
        const cachedFilters = localStorage.getItem(FILTERS_CACHE_KEY);
        if (cachedFilters != null) {
            onChangeFilters(JSON.parse(cachedFilters), true);
        } else {
            onChangeFilters({}, true);
        }
    };

    const onChangeFilters = (_filters: Filters, isOnLoad?: boolean) => {
        updateTotalFilters(_filters);
        setFilters({ ..._filters });
        localStorage.setItem(FILTERS_CACHE_KEY, JSON.stringify(_filters));

        setCriteria(prev => ({
            ...prev,
            vehicleId: _filters.vehicleId,
            type: _filters.type,
            driverResponsibleId: _filters.driverResponsibleId,
            startDate: _filters.startDate,
            endDate: _filters.endDate,
            tripStartDate: _filters.tripStartDate,
            tripEndDate: _filters.tripEndDate,
            interventionType: _filters.interventionType,
            annotationStatus: _filters.annotationStatus,
            localIds: _filters.localIds,
            localOperator: _filters.localOperator,
            segmentId: _filters.segmentId,
            orderBy: _filters.orderBy,
            orderColumn: _filters.orderColumn,
            page: (isOnLoad && _filters.page) ? _filters.page : 1,
        }));

        isFirstLoad.current = false;
    };

    const goToDetails = (id: string) => {
        navigateTo('details', id);
    };

    const goToEdit = (id: string) => {
        if (hasVerificationsWritePolicy) {
            navigateTo('edit', id);
        } else {
            goToDetails(id);
        }
    };

    const navigateTo = (typeUrl?: string, id?: string) => {
        if (typeUrl) {
            history.push(typeUrl === 'new' ? '/verifications/new' : `/verifications/${typeUrl}/${id}`);
        } else {
            history.push('/verifications');
        }
    };

    useEffect(() => {
        loadFilters();
    }, []);

    const onRowClick = (item: CarVerificationListItemDto, event: any) => {
        event.preventDefault();
        event.stopPropagation();

        if (event.ctrlKey) {
            window.open(`/verifications/details/${item.id}`, '_blank');
            return;
        }
        history.push(`/verifications/details/${item.id}`);
    };

    const filterItems = useMemo<FilterBadgeItem[]>(() => {
        const items: FilterBadgeItem[] = [];

        if (filters.vehicleId && filters.vehicleName) {
            items.push({
                key: 'vehicle',
                label: t('vehicle.vehicle') as string,
                value: filters.vehicleName,
                onRemove: () => onChangeFilters({ ...filters, vehicleId: undefined, vehicleName: undefined })
            });
        }

        if (filters.type) {
            items.push({
                key: 'type',
                label: t('common.type') as string,
                value: t(`car_verification.annotation_types.${filters.type}`.toLowerCase() as any) as string,
                onRemove: () => onChangeFilters({ ...filters, type: undefined })
            });
        }

        if (filters.driverResponsibleId && filters.driverResponsibleName) {
            items.push({
                key: 'driver',
                label: t('car_verification.list.driver') as string,
                value: filters.driverResponsibleName,
                onRemove: () => onChangeFilters({ ...filters, driverResponsibleId: undefined, driverResponsibleName: undefined })
            });
        }

        if (filters.interventionType) {
            items.push({
                key: 'interventionType',
                label: t('common.intervention_type') as string,
                value: t(`car_verification.intervention_type.${filters.interventionType}`.toLowerCase() as any) as string,
                onRemove: () => onChangeFilters({ ...filters, interventionType: undefined })
            });
        }

        if (filters.annotationStatus) {
            items.push({
                key: 'annotationStatus',
                label: t('common.annotation_status') as string,
                value: t(`car_verification.annotation_status.${filters.annotationStatus}`.toLowerCase() as any) as string,
                onRemove: () => onChangeFilters({ ...filters, annotationStatus: undefined })
            });
        }

        if (filters.startDate || filters.endDate) {
            const start = filters.startDate ? dayjs(filters.startDate).format(DATE_FORMAT_DEFAUT) : '';
            const end = filters.endDate ? dayjs(filters.endDate).format(DATE_FORMAT_DEFAUT) : '';
            items.push({
                key: 'verificationDate',
                label: t('car_verification.filter_date') as string,
                value: start && end ? `${t('common.range.from')} ${start} ${t('common.range.to')} ${end}` : start || end,
                onRemove: () => onChangeFilters({ ...filters, startDate: undefined, endDate: undefined })
            });
        }

        if (filters.tripStartDate || filters.tripEndDate) {
            const start = filters.tripStartDate ? dayjs(filters.tripStartDate).format(DATE_FORMAT_DEFAUT) : '';
            const end = filters.tripEndDate ? dayjs(filters.tripEndDate).format(DATE_FORMAT_DEFAUT) : '';
            items.push({
                key: 'tripDate',
                label: t('car_verification.trip_filter_date') as string,
                value: start && end ? `${t('common.range.from')} ${start} ${t('common.range.to')} ${end}` : start || end,
                onRemove: () => onChangeFilters({ ...filters, tripStartDate: undefined, tripEndDate: undefined })
            });
        }

        if (filters.segmentId && filters.segmentName) {
            items.push({
                key: 'segment',
                label: t('vehicle.type') as string,
                value: filters.segmentName,
                onRemove: () => onChangeFilters({ ...filters, segmentId: undefined, segmentName: undefined })
            });
        }

        if (filters.localIds?.length && filters.localNames?.length) {
            items.push({
                key: 'local',
                label: `${t('vehicle.local')} (${filters.localOperator === 'AND' ? t('common.filter_all' as any) : t('common.filter_any' as any)})` as string,
                value: filters.localNames.join(', '),
                onRemove: () => onChangeFilters({ ...filters, localIds: [], localNames: [] })
            });
        }

        return items;
    }, [filters, t]);

    const getHeaderTitle = () => {
        if (!type) return t('car_verification.title');
        if (type === 'new') return t('car_verification.titles.new');
        if (type === 'details') return t('car_verification.titles.details');
        if (type === 'edit') return t('car_verification.titles.edit');
        return t('car_verification.title');
    };

    return (
        <ScreenTitle title={t('car_verification.title')}>
            <CompactPageLayout
                title={getHeaderTitle() as string}
                onBack={type ? () => history.goBack() : undefined}
                contentAreaClassName={styles.contentArea}
                middleContent={!type ? <FilterBadgesOverflow items={filterItems} align="right" /> : undefined}
                headerButtons={
                    !type ? (
                        <>
                            <Popover
                                contentContainerClassName={styles.filtersPopoverContainer}
                                containerStyle={{ boxShadow: '0 4px 14px rgba(0, 0, 0, 0.1)', backgroundColor: '#fff' }}
                                onClickOutside={() => updateTotalFilters(filters)}
                                content={setIsPopoverOpen => <VerificationFiltersScreen
                                    genericFilter={true}
                                    filters={filters}
                                    onFilter={_filters => { setIsPopoverOpen(false); onChangeFilters(_filters) }}
                                    onChange={_filters => updateTotalFilters(_filters)}
                                />}
                            >
                                {(isPopoverOpen, setIsPopoverOpen) => (
                                    <ScreenHeaderButton icon={FiltersIcon} onClick={() => setIsPopoverOpen(!isPopoverOpen)}>
                                        {filtersTotal > 0 &&
                                            <div className={styles.counterList}> <div className={styles.counterNumber}>{filtersTotal}</div> </div>
                                        }
                                    </ScreenHeaderButton>
                                )}
                            </Popover>
                            <ExportListingButton exportRef={exportListingRef} />
                            {hasVerificationsWritePolicy && <ScreenHeaderButton icon={AddIcon} onClick={() => navigateTo('new')} />}
                        </>
                    ) : undefined
                }
            >
                {/* LIST */}
                {!type && !isFirstLoad.current &&
                    <ListVerificationsScreen
                        editable={true}
                        criteria={criteria}
                        onClickItem={onRowClick}
                        exportRef={exportListingRef}
                    />
                }
                {/* DETAILS / EDIT / NEW */}
                {type && <Box className={styles.box}>
                    <CarVerification
                        editable={true}
                        goToEdit={goToEdit}
                        genericPage
                        type={type}
                        vehicleId={null}
                        verificationId={itemId}
                    />
                </Box>}
            </CompactPageLayout>
        </ScreenTitle>
    );
}

export default VerificationScreen;
